import { useEffect, useState, useRef, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type ArtefactOutline, type Bid } from '../lib/api'
import { useBidProfile } from '../lib/bid-profile'
import { getRecentBidIds, recordBidVisit } from '../lib/recent-bids'
import { partLabel, useOutline } from '../lib/outline'
import { useBetaTester } from '../lib/beta'
import type { PartKind } from '../lib/grid'
import { allowedPartKinds, artefactTypeOf, type ArtefactType } from '../lib/artefact-type'
import {
  acceptsSubfolder,
  buildExplorerTree,
  canDropFolder,
  dropIndexBefore,
  filterExplorerTree,
  findFolderNode,
  folderDropZone,
  siblingFolderIds,
  MAX_FOLDER_DEPTH,
  type FolderDropZone,
  type FolderNode,
} from '../lib/folders'
import { isTemplateLibrary, LIBRARY_MAX_FOLDER_DEPTH, useTemplateLibrary } from '../lib/template-library'
import { PromptModal } from './PromptModal'
import { NewArtefactModal } from './NewArtefactModal'
import { NewDeckFromPowerPoint } from './ImportPowerPointDialog'
import { ConfirmModal } from './ConfirmModal'
import { ExternalAgentModal } from './ExternalAgentModal'
import { PublishTemplateDialog } from './PublishTemplateDialog'
import { ArtefactTemplateDialog } from './ArtefactTemplateDialog'
import { Toasts, type Toast } from './Toasts'
import { IconClose, IconFolder, IconPaste, IconPlus, IconSearch } from './NavIcons'
import { ArtefactTypeGlyph } from './ArtefactTypeIcon'
import type { View } from '../App'
import { endSlideDrag, isCopyDrag, requestSlideFocus, slideDragOf, type SlideDrag } from '../slides/transfer'

interface LeftNavProps {
  view: View
  onNavigate: (v: View) => void
  /** Navigate to the bid recap/overview (BidHome). */
  onOverview: () => void
  bids: Bid[]
  activeBid: string | null
  onSelectBid: (id: string) => void
  openRoom: string | null
  onOpenPart: (name: string) => void
  /** Called after a delete so the App can close the editor if the open room was removed. */
  onRoomsDeleted: (rooms: string[]) => void
}

type Modal =
  | { kind: 'new-artefact' }
  | { kind: 'new-from-pptx'; title: string; folderId: string | null }
  | { kind: 'add-item'; artefactId: string; artefactType: ArtefactType }
  | { kind: 'rename-artefact'; artefactId: string; current: string }
  | { kind: 'rename-item'; room: string; current: string }
  | { kind: 'split-parts'; artefactId: string }
  | { kind: 'delete-artefact'; artefactId: string; rooms: string[] }
  | { kind: 'delete-item'; room: string }
  | { kind: 'external'; artefactId: string; title: string; parts: { room: string; title: string }[] }
  | { kind: 'new-folder'; parentFolderId: string | null }
  | { kind: 'rename-folder'; folderId: string; current: string }
  | { kind: 'delete-folder'; folderId: string }
  | { kind: 'publish-template'; artefactId: string; title: string; artefactType: ArtefactType }
  | { kind: 'template-description'; templateId: string; current: string }
  | { kind: 'office-templates'; artefactId: string }
  | null

/**
 * What is currently being dragged. Three kinds share the panel, and each accepts a different set
 * of drop targets — a part only ever reorders inside its own artefact, while artefacts and folders
 * move through the folder tree.
 */
type Drag =
  | { kind: 'part'; artefactId: string; index: number }
  | { kind: 'artefact'; artefactId: string }
  | { kind: 'folder'; folderId: string }

/** How long a slide must hover a collapsed artefact before it opens (AB#207). */
const SLIDE_HOVER_EXPAND_MS = 700

const PART_KIND_LABEL: Record<PartKind, 'md_kind' | 'grid_kind' | 'slides_kind'> = {
  markdown: 'md_kind',
  grid: 'grid_kind',
  slides: 'slides_kind',
}

/** The part kinds "Add part" offers: what the artefact's type allows, minus grid for a non-tester. */
function addableKinds(type: ArtefactType, isBeta: boolean): PartKind[] {
  return allowedPartKinds(type).filter((k) => k !== 'grid' || isBeta)
}

export function LeftNav({
  view,
  onNavigate,
  onOverview,
  bids,
  activeBid,
  onSelectBid,
  openRoom,
  onOpenPart,
  onRoomsDeleted,
}: LeftNavProps) {
  const { t } = useLang()
  // A viewer may read every artefact of the bid but change none of them, so the create buttons and
  // the per-row action menus (whose entries mutate) are not shown — except an artefact's template
  // entry, which opens read-only for a viewer. The server refuses every write anyway — this is only
  // about not offering a button that answers 403.
  const { isViewer, isTenantAdmin } = useBidProfile(activeBid)
  // The template library (AB#269) opens in this same explorer: its artefacts are the templates,
  // its folders their categories — 2 levels at most, and no Knowledge sources.
  const { isLibrary } = useTemplateLibrary(activeBid)
  const maxDepth = isLibrary ? LIBRARY_MAX_FOLDER_DEPTH : MAX_FOLDER_DEPTH
  // Each template's description and version, from the catalogue (keyed by template = artefact id).
  const templateCatalogue = useQuery({
    queryKey: ['artefact-templates'],
    queryFn: () => api.artefactTemplates(),
    enabled: isLibrary,
  })
  const templateMeta = useMemo(
    () => new Map((templateCatalogue.data?.templates ?? []).map((tpl) => [tpl.id, tpl])),
    [templateCatalogue.data],
  )
  // "Publish as template…" (artefact templates 6/8): tenant admins only — an affordance, the backend
  // checks again — and never inside the template library itself, whose artefacts already ARE templates.
  const canPublishTemplate = isTenantAdmin && !isLibrary && !isTemplateLibrary(activeBid)
  const [toasts, setToasts] = useState<Toast[]>([])
  const qc = useQueryClient()
  const editor = view === 'editor'

  // Track visits for the switcher below: most-recently-visited bids first, not just bid order.
  useEffect(() => {
    if (activeBid) recordBidVisit(activeBid)
  }, [activeBid])

  // Switcher chips: the 3 most recently visited OTHER bids (never the active one — it is what the
  // breadcrumb already names). A bid never visited yet (just shared with the member, still only in
  // `bids`) sorts after every recorded one, in its existing (server) order.
  const otherBids = useMemo(() => bids.filter((b) => b.id !== activeBid), [bids, activeBid])
  const recentOtherBids = useMemo(() => {
    const rank = new Map(getRecentBidIds().map((id, i) => [id, i]))
    return [...otherBids].sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity))
  }, [otherBids, activeBid])
  const shownBids = recentOtherBids.slice(0, 3)
  const hasMoreBids = recentOtherBids.length > shownBids.length

  const [modal, setModal] = useState<Modal>(null)
  const [menuFor, setMenuFor] = useState<string | null>(null)
  // Explicit expand/collapse per artefact; absent = the default (synthetic working-docs and
  // the open artefact / first one start open).
  const [toggled, setToggled] = useState<Record<string, boolean>>({})
  // Explicit expand/collapse per folder; absent = open, so a new folder shows its content.
  const [folderOpen, setFolderOpen] = useState<Record<string, boolean>>({})
  // The artefact waiting to be pasted — the keyboard-reachable twin of dragging it.
  const [clipboard, setClipboard] = useState<string | null>(null)
  // The selected folder — where the two "new" buttons create. `null` = the explorer's root. A folder is selected on the FIRST click; the second one
  // expands/collapses it, so picking a destination never costs the user their open tree.
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null)
  // Whether the member explicitly picked the Artefacts entry. The root being the destination is
  // the default, so it alone must not highlight the entry; and once an artefact is opened (or the
  // view changes) that artefact's row is "where you are" — one highlight, not two.
  const [rootPicked, setRootPicked] = useState(false)
  const [pickedAt, setPickedAt] = useState({ openRoom, view })
  if (pickedAt.openRoom !== openRoom || pickedAt.view !== view) {
    setPickedAt({ openRoom, view })
    setRootPicked(false)
  }
  const rootHighlighted = rootPicked && selectedFolder === null
  // The Dashboard entry is "where you are" only while nothing in the explorer is: picking the
  // Artefacts entry or a folder moves the one highlight there, and clicking Dashboard takes it back.
  const dashboardActive = editor && !openRoom && !rootPicked && selectedFolder === null
  const pickDashboard = (): void => {
    setRootPicked(false)
    setSelectedFolder(null)
    onOverview()
  }
  // Knowledge sources follows the same one-highlight rule: picking the Artefacts entry or a folder
  // while on that page moves the highlight there, and clicking the entry takes it back.
  const knowledgeActive = view === 'knowledge' && !rootPicked && selectedFolder === null
  const pickKnowledge = (): void => {
    if (!activeBid) return
    setRootPicked(false)
    setSelectedFolder(null)
    onNavigate('knowledge')
  }
  // The last part opened, kept while none is (Dashboard, Knowledge sources…): its artefact's
  // default expansion follows it, so leaving the editor does not fold the tree the member had open.
  const [lastRoom, setLastRoom] = useState(openRoom)
  if (openRoom && openRoom !== lastRoom) setLastRoom(openRoom)
  // The search field is folded into a button until it is asked for.
  const [searching, setSearching] = useState(false)
  const [query, setQuery] = useState('')

  // The artefact/part tree lives in lib/outline.ts — the breadcrumb names the same titles.
  const { artefacts: allArtefacts, folders, loading: outlineLoading } = useOutline(activeBid)
  // Grid parts are in beta — only a tester is offered that kind (see lib/beta.ts). Slides are released.
  const { isBeta } = useBetaTester()
  const tree = useMemo(() => buildExplorerTree(folders, allArtefacts), [folders, allArtefacts])
  // What is rendered: the whole tree, or the part of it that matches the search.
  const filtering = query.trim() !== ''
  const shown = useMemo(() => filterExplorerTree(tree, query), [tree, query])
  // Resolved against the live tree, so a selection pointing at a folder a teammate deleted
  // (or one the search has hidden — it is still a valid destination) degrades to the root.
  const selectedNode = selectedFolder !== null ? findFolderNode(tree.roots, selectedFolder) : null
  const createIn = selectedNode?.folder.folderId ?? null
  const canAddFolder = selectedNode === null || acceptsSubfolder(selectedNode.depth, maxDepth)

  function closeSearch(): void {
    setSearching(false)
    setQuery('')
  }

  const presence = useQuery({
    queryKey: ['presence', activeBid],
    queryFn: () => api.presence(activeBid as string),
    enabled: Boolean(activeBid) && editor,
    refetchInterval: 5000,
  })
  const roomUsers = presence.data?.rooms ?? {}
  const partUsers = (room: string): string[] => roomUsers[room]?.users ?? []

  const refresh = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['docs', activeBid] })
    await qc.invalidateQueries({ queryKey: ['folders', activeBid] })
  }

  // CRUD handlers — each refreshes the outline and closes the dialog/menu.
  /** Lands in the selected folder (root when none is selected) — see `createIn`. */
  /** `templateId` set = a copy of that library template (its type wins — AB#270). */
  async function createArtefact(title: string, type: ArtefactType, templateId: string | null): Promise<void> {
    if (!activeBid) return
    const res = templateId
      ? await api.createArtefactFromTemplate(activeBid, title, createIn, templateId)
      : await api.createArtefact(activeBid, title, createIn, type)
    setModal(null)
    if (createIn) setFolderOpen((prev) => ({ ...prev, [createIn]: true }))
    await refresh()
    // A template with no part yields an artefact with no room to open.
    if (res.room) onOpenPart(res.room)
  }
  /** `kind` is fixed at creation and never changes afterwards — see api.createPart. */
  async function addItem(artefactId: string, title: string, kind: PartKind = 'markdown'): Promise<void> {
    if (!activeBid) return
    const res = await api.createPart(activeBid, artefactId, title, kind)
    setModal(null)
    await refresh()
    onOpenPart(res.room)
  }
  /** "Parts from sections" — one part per top-level § across the whole artefact. The § numbers
   *  themselves do not move (they are cut at the roots), only the parts holding them. */
  async function splitParts(artefactId: string): Promise<void> {
    if (!activeBid) return
    await api.splitParts(activeBid, artefactId)
    setModal(null)
    await refresh()
  }
  async function renameArtefact(artefactId: string, title: string): Promise<void> {
    if (!activeBid) return
    await api.renameArtefact(activeBid, artefactId, title)
    setModal(null)
    await refresh()
  }
  async function renameItem(room: string, title: string): Promise<void> {
    await api.renamePart(room, title)
    setModal(null)
    await refresh()
  }

  // ─── Folder CRUD ───────────────────────────────────────────────────────
  async function createFolder(parentFolderId: string | null, title: string): Promise<void> {
    if (!activeBid) return
    await api.createFolder(activeBid, title, parentFolderId)
    setModal(null)
    if (parentFolderId) setFolderOpen((prev) => ({ ...prev, [parentFolderId]: true }))
    await refresh()
  }
  async function renameFolder(folderId: string, title: string): Promise<void> {
    if (!activeBid) return
    await api.renameFolder(activeBid, folderId, title)
    setModal(null)
    await refresh()
  }
  /** Delete a folder — the backend lifts its artefacts and subfolders one level, never deletes. */
  async function deleteFolder(folderId: string): Promise<void> {
    if (!activeBid) return
    await api.deleteFolder(activeBid, folderId)
    setModal(null)
    // Creating into a folder that no longer exists would silently land at the root: say so by
    // moving the selection back there.
    if (selectedFolder === folderId) setSelectedFolder(null)
    await refresh()
  }

  // ─── Moving things around the tree ─────────────────────────────────────
  /** Move an artefact into a folder (null = root); `index` is its slot among that folder's artefacts. */
  async function placeArtefact(artefactId: string, folderId: string | null, index?: number): Promise<void> {
    if (!activeBid) return
    setClipboard(null)
    if (folderId) setFolderOpen((prev) => ({ ...prev, [folderId]: true }))
    await api.moveArtefact(activeBid, artefactId, folderId, index)
    await refresh()
  }
  async function placeFolder(folderId: string, parentFolderId: string | null, index?: number): Promise<void> {
    if (!activeBid) return
    if (parentFolderId) setFolderOpen((prev) => ({ ...prev, [parentFolderId]: true }))
    await api.moveFolder(activeBid, folderId, parentFolderId, index)
    await refresh()
  }

  // ─── Drag-and-drop ─────────────────────────────────────────────────────
  // One ref for what is moving, one state for the row highlighted underneath it.
  const drag = useRef<Drag | null>(null)
  const [dropKey, setDropKey] = useState<string | null>(null)
  // Which edge of `dropKey`'s row a folder-over-folder drag is hovering — see handleFolderDragOver.
  const [folderZone, setFolderZone] = useState<FolderDropZone | null>(null)

  function endDrag(): void {
    drag.current = null
    setDropKey(null)
    setFolderZone(null)
  }
  function clearDrop(): void {
    setDropKey(null)
    setFolderZone(null)
  }
  /** Accept the drop on this row and mark it; ignoring the event lets the browser refuse it. */
  function accept(e: React.DragEvent, key: string, zone: FolderDropZone | null = null): void {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDropKey(key)
    setFolderZone(zone)
  }

  // A SLIDE dragged out of the slides editor's sorter (slides/transfer.ts, AB#207), or its selection
  // (AB#472), is accepted only by another slides part the member may edit, and never while the tree
  // is filtered. A plain drop moves the slide, Ctrl (Alt / Option on macOS) copies it; the cursor
  // shows which.
  function slideDropTarget(e: React.DragEvent, part: { name: string; kind?: PartKind }): boolean {
    const slide = slideDragOf(e.dataTransfer)
    return !!slide && !drag.current && !isViewer && !filtering && part.kind === 'slides' && part.name !== slide.room
  }
  // A collapsed artefact holding a slides part opens after a moment of hovering it with a slide, so
  // its parts become reachable as drop targets. The row itself never accepts the slide.
  const expandTimer = useRef<{ artefactId: string; timer: ReturnType<typeof setTimeout> } | null>(null)
  function cancelExpand(): void {
    if (expandTimer.current) clearTimeout(expandTimer.current.timer)
    expandTimer.current = null
  }
  useEffect(() => cancelExpand, [])
  function expandOnSlideHover(e: React.DragEvent, d: ArtefactOutline, expanded: boolean): void {
    if (expanded || drag.current || filtering || !slideDragOf(e.dataTransfer)) return
    if (!d.parts.some((p) => p.kind === 'slides')) return
    if (expandTimer.current?.artefactId === d.artefactId) return
    cancelExpand()
    expandTimer.current = {
      artefactId: d.artefactId,
      timer: setTimeout(() => {
        expandTimer.current = null
        setToggled((prev) => ({ ...prev, [d.artefactId]: true }))
      }, SLIDE_HOVER_EXPAND_MS),
    }
  }
  function leaveArtefactRow(e: React.DragEvent): void {
    clearDrop()
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) cancelExpand()
  }
  function dropSlide(e: React.DragEvent, part: { name: string; title: string; section?: string | null }): void {
    const dropped = slideDragOf(e.dataTransfer)
    e.preventDefault()
    e.stopPropagation()
    clearDrop()
    endSlideDrag()
    if (!dropped) return
    const mode = isCopyDrag(e) ? 'copy' : 'move'
    void transferSlides(dropped, part, mode)
  }
  /** Send the dragged slides, one at a time and in deck order: each lands at the END of the target. */
  async function transferSlides(dropped: SlideDrag, part: { name: string; title: string; section?: string | null }, mode: 'move' | 'copy'): Promise<void> {
    const landed: Array<{ room: string; slide: string }> = []
    for (const slide of dropped.slides) {
      // request() already told the member why a refused transfer failed (409: an AI holds the slide…).
      const r = await api.transferSlide(dropped.room, slide, part.name, mode).catch(() => null)
      if (r) landed.push(r)
    }
    const first = landed[0]
    if (!first) return
    const copy = mode === 'copy'
    const said = landed.length > 1 ? t(copy ? 'sl_slides_copied_to' : 'sl_slides_moved_to').replace('{count}', String(landed.length)) : t(copy ? 'sl_slide_copied_to' : 'sl_slide_moved_to')
    const skipped = (dropped.skipped ?? 0) + dropped.slides.length - landed.length
    const message = said.replace('{part}', partLabel(part)) + (skipped ? ` · ${t('sl_slides_left').replace('{count}', String(skipped))}` : '')
    // The target opens as a click on its row does; its editor selects the first slide once it arrives.
    requestSlideFocus({ room: first.room, slide: first.slide, message, ...(skipped ? { notice: true } : {}) })
    setRootPicked(false)
    onOpenPart(first.room)
  }

  // Parts: reorder within their own artefact (unchanged behaviour — a part never leaves it).
  function handlePartDragOver(e: React.DragEvent, artefactId: string, part: { name: string; kind?: PartKind }): void {
    const src = drag.current
    if (src?.kind === 'part' && src.artefactId === artefactId) accept(e, part.name)
    else if (slideDropTarget(e, part)) {
      e.preventDefault()
      e.dataTransfer.dropEffect = isCopyDrag(e) ? 'copy' : 'move'
      setDropKey(part.name)
    }
  }
  function handlePartDrop(
    e: React.DragEvent,
    artefactId: string,
    parts: { name: string; title: string; kind?: PartKind; section?: string | null }[],
    toIndex: number,
  ): void {
    const target = parts[toIndex]
    if (target && slideDropTarget(e, target)) {
      dropSlide(e, target)
      return
    }
    e.preventDefault()
    e.stopPropagation()
    const src = drag.current
    endDrag()
    if (!src || src.kind !== 'part' || src.artefactId !== artefactId || !activeBid) return
    if (src.index === toIndex) return
    const reordered = parts.map((p) => p.name)
    const [item] = reordered.splice(src.index, 1)
    reordered.splice(toIndex, 0, item!)
    // request() already told the member if the reorder failed; without the catch, the rejection
    // escaped and added an "Unexpected error" dialog on top of it.
    void api
      .reorderParts(activeBid, artefactId, reordered)
      .then(() => refresh())
      .catch(() => undefined)
  }

  // Artefact rows accept another artefact — it lands in this artefact's folder, at its slot.
  function handleArtefactDragOver(e: React.DragEvent, d: ArtefactOutline): void {
    const src = drag.current
    if (src?.kind === 'artefact' && src.artefactId !== d.artefactId) accept(e, `artefact:${d.artefactId}`)
  }
  /** `siblings` is the row list `d` was rendered in — the index space the drop indicator points at. */
  function handleArtefactDrop(
    e: React.DragEvent,
    d: ArtefactOutline,
    index: number,
    siblings: ArtefactOutline[],
  ): void {
    const src = drag.current
    if (!src || src.kind !== 'artefact' || src.artefactId === d.artefactId) return
    e.preventDefault()
    e.stopPropagation()
    endDrag()
    const at = dropIndexBefore(siblings.map((a) => a.artefactId), src.artefactId, index)
    void placeArtefact(src.artefactId, d.folderId, at)
  }

  /** Which zone of the hovered row a folder-over-folder drag is in, from raw pointer geometry —
   *  independent of any state, so drag-over and drop always agree on the same answer even when a
   *  dragLeave in between left the visual indicator stale. */
  function folderRowZone(e: React.DragEvent): FolderDropZone {
    const rect = e.currentTarget.getBoundingClientRect()
    return folderDropZone(e.clientY - rect.top, rect.height)
  }

  // Folder rows accept an artefact (dropped in at the end), and another folder either NESTED
  // inside (dropped on the row's middle) or reordered as its SIBLING (dropped on a top/bottom
  // edge) — see folderDropZone. The edge zones are what let a folder change rank among its current
  // siblings without being re-parented into them.
  function handleFolderDragOver(e: React.DragEvent, node: FolderNode): void {
    const src = drag.current
    if (!src) return
    if (src.kind === 'artefact') {
      accept(e, `folder:${node.folder.folderId}`)
      return
    }
    if (src.kind !== 'folder' || src.folderId === node.folder.folderId) return
    const zone = folderRowZone(e)
    const targetParent = zone === 'nest' ? node.folder.folderId : node.folder.parentFolderId
    const targetDepth = zone === 'nest' ? node.depth : node.depth - 1
    if (canDropFolder(folders, src.folderId, targetParent, targetDepth, maxDepth)) {
      accept(e, `folder:${node.folder.folderId}`, zone)
    }
  }
  function handleFolderDrop(e: React.DragEvent, node: FolderNode): void {
    const src = drag.current
    if (!src || src.kind === 'part') return
    e.preventDefault()
    e.stopPropagation()
    const zone = folderRowZone(e)
    endDrag()
    if (src.kind === 'artefact') {
      void placeArtefact(src.artefactId, node.folder.folderId)
      return
    }
    if (src.folderId === node.folder.folderId) return
    if (zone === 'nest') {
      if (canDropFolder(folders, src.folderId, node.folder.folderId, node.depth, maxDepth)) {
        void placeFolder(src.folderId, node.folder.folderId)
      }
      return
    }
    // Top/bottom edge: land as NODE's sibling — same parent as node, ranked just before/after it —
    // rather than nested inside it.
    const parentId = node.folder.parentFolderId
    if (!canDropFolder(folders, src.folderId, parentId, node.depth - 1, maxDepth)) return
    const siblingIds = siblingFolderIds(folders, parentId)
    const targetIndex = siblingIds.indexOf(node.folder.folderId) + (zone === 'after' ? 1 : 0)
    void placeFolder(src.folderId, parentId, dropIndexBefore(siblingIds, src.folderId, targetIndex))
  }

  // The section header doubles as the root drop zone — the way back out of every folder.
  function handleRootDragOver(e: React.DragEvent): void {
    const src = drag.current
    if (src && src.kind !== 'part') accept(e, 'root')
  }
  function handleRootDrop(e: React.DragEvent): void {
    const src = drag.current
    if (!src || src.kind === 'part') return
    e.preventDefault()
    endDrag()
    if (src.kind === 'artefact') void placeArtefact(src.artefactId, null)
    else void placeFolder(src.folderId, null)
  }

  async function deleteArtefact(artefactId: string, rooms: string[]): Promise<void> {
    if (!activeBid) return
    await api.deleteArtefact(activeBid, artefactId)
    setModal(null)
    await refresh()
    onRoomsDeleted(rooms)
  }
  /** A template's description (PATCH /v1/artefact-templates/:id) — the version does not move. */
  async function saveTemplateDescription(templateId: string, description: string): Promise<void> {
    await api.updateArtefactTemplate(templateId, { description })
    setModal(null)
    await qc.invalidateQueries({ queryKey: ['artefact-templates'] })
  }
  async function deleteItem(room: string): Promise<void> {
    await api.deletePart(room)
    setModal(null)
    await refresh()
    onRoomsDeleted([room])
  }

  const toggleMenu = (id: string, e: React.MouseEvent): void => {
    e.stopPropagation()
    setMenuFor((m) => (m === id ? null : id))
  }

  /** Right-click on a folder/artefact/part row opens its "…" menu in place of the browser's own
   *  context menu — always OPENS (never toggles: a right-click always means "show me the actions
   *  here", unlike the kebab button, which is also this row's close affordance). */
  const openMenuOnContext = (id: string, e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    setMenuFor(id)
  }

  // A click (or right-click — `mousedown` fires for every button) ANYWHERE outside the open "…"
  // menu closes it — the whole app, not just this panel, since a stray click in the editor or
  // chat should dismiss it too. A plain document listener rather than a full-screen catcher div:
  // it does not depend on this menu happening to have the topmost z-index at the click point, and
  // it does not swallow the click itself (clicking a DIFFERENT row both closes this menu and
  // still acts on that row).
  useEffect(() => {
    if (!menuFor) return
    const closeIfOutside = (e: MouseEvent): void => {
      const target = e.target as Element | null
      if (target?.closest('.rowmenu, .row-kebab')) return
      setMenuFor(null)
    }
    document.addEventListener('mousedown', closeIfOutside)
    return () => document.removeEventListener('mousedown', closeIfOutside)
  }, [menuFor])

  /**
   * One artefact row plus (when expanded) its parts. `index` is its slot inside `siblings`, the
   * row list it is rendered in — a drop onto it is resolved against that same list.
   */
  function renderArtefact(d: ArtefactOutline, index: number, siblings: ArtefactOutline[]) {
    const isSynthetic = d.artefactId.startsWith('__')
    const open = editor && d.parts.some((p) => p.name === openRoom)
    // Not tied to the view: switching page must not change which artefacts are expanded.
    const defaultOpen = isSynthetic || d.parts.some((p) => p.name === lastRoom) || allArtefacts[0] === d
    const expanded = d.artefactId in toggled ? toggled[d.artefactId]! : defaultOpen
    const artefactUsers = [...new Set(d.parts.flatMap((p) => partUsers(p.name)))]
    const artefactMenuId = `artefact:${d.artefactId}`
    // A deck has a default PowerPoint template, a prose artefact a Word template (AB#444); a grid none.
    const artefactType = artefactTypeOf(d.type)
    const templateEntry = artefactType !== 'grid' && (
      <button role="menuitem" onClick={() => { setMenuFor(null); setModal({ kind: 'office-templates', artefactId: d.artefactId }) }}>
        {t(artefactType === 'slides' ? 'art_tpl_menu_slides' : 'art_tpl_menu_word')}
      </button>
    )
    const toggleArtefact = (): void => {
      setRootPicked(false)
      onNavigate('editor')
      // Coming from another page (Knowledge sources…), the click is "go to this artefact", never
      // "fold it": folding it would land on the bid home and light Dashboard instead of this row.
      const willExpand = !editor || !expanded
      setToggled((prev) => ({ ...prev, [d.artefactId]: willExpand }))
      // Expanding also opens the artefact's first part — one click to start writing, instead of
      // opening the list and then having to pick the part that was already the obvious one.
      if (willExpand && d.parts.length > 0) onOpenPart(d.parts[0]!.name)
    }
    return (
      <div key={d.artefactId}>
        <div
          className={
            'item' +
            (open ? ' active' : '') +
            (clipboard === d.artefactId ? ' cut' : '') +
            (dropKey === artefactMenuId ? ' drop-before' : '')
          }
          draggable={!isSynthetic && !filtering}
          onDragStart={isSynthetic ? undefined : () => { drag.current = { kind: 'artefact', artefactId: d.artefactId } }}
          onDragEnd={isSynthetic ? undefined : endDrag}
          onDragOver={(e) => {
            handleArtefactDragOver(e, d)
            expandOnSlideHover(e, d, expanded)
          }}
          onDragLeave={leaveArtefactRow}
          onDrop={(e) => handleArtefactDrop(e, d, index, siblings)}
          onClick={toggleArtefact}
          onContextMenu={isSynthetic || isViewer ? undefined : (e) => openMenuOnContext(artefactMenuId, e)}
        >
          <span className={'twisty' + (expanded ? ' open' : '')} aria-hidden="true">▸</span>
          {/* A slides deliverable (AB#197) reads as a deck at a glance; the glyph is decorative and
              the accessible name carries the word, like the part-kind glyphs below. */}
          {d.type === 'slides' ? (
            <span className="gi" title={t('artefact_type_slides_label')}>
              <span aria-hidden="true"><ArtefactTypeGlyph type="slides" /></span>
              <span className="visually-hidden">{t('artefact_type_slides_label')} — </span>
            </span>
          ) : d.type === 'grid' ? (
            <span className="gi" title={t('artefact_type_grid_label')}>
              <span aria-hidden="true"><ArtefactTypeGlyph type="grid" /></span>
              <span className="visually-hidden">{t('artefact_type_grid_label')} — </span>
            </span>
          ) : (
            <span className="gi"><ArtefactTypeGlyph type="prose" /></span>
          )}{' '}
          <span className="lbl">{d.title}</span>
          {artefactUsers.length > 0 && (
            <span className="pcount" title={artefactUsers.join(', ')} aria-label={`${artefactUsers.length} editing`}>
              👤 {artefactUsers.length}
            </span>
          )}
          <span className="ct">{d.parts.length}</span>
          {!isSynthetic && (
            <span className="rowmenu-wrap">
              {(!isViewer || templateEntry) && (
              <button
                className="row-kebab"
                aria-label={t('artefact_actions')}
                aria-haspopup="menu"
                aria-expanded={menuFor === artefactMenuId}
                onClick={(e) => toggleMenu(artefactMenuId, e)}
              >
                ⋯
              </button>
              )}
              {menuFor === artefactMenuId && isViewer && (
                <div className="rowmenu" role="menu" onClick={(e) => e.stopPropagation()}>
                  {templateEntry}
                </div>
              )}
              {menuFor === artefactMenuId && !isViewer && (
                <div className="rowmenu" role="menu" onClick={(e) => e.stopPropagation()}>
                  {/* Nothing to add to a grid deliverable for a member outside the grid beta. */}
                  {addableKinds(artefactTypeOf(d.type), isBeta).length > 0 && (
                    <button role="menuitem" onClick={() => { setMenuFor(null); setModal({ kind: 'add-item', artefactId: d.artefactId, artefactType: artefactTypeOf(d.type) }) }}>{t('add_item')}</button>
                  )}
                  <button role="menuitem" onClick={() => { setMenuFor(null); setModal({ kind: 'split-parts', artefactId: d.artefactId }) }}>{t('parts_from_sections')}</button>
                  <button role="menuitem" onClick={() => { setMenuFor(null); setModal({ kind: 'rename-artefact', artefactId: d.artefactId, current: d.title }) }}>{t('rename')}</button>
                  {templateEntry}
                  <button role="menuitem" onClick={() => { setMenuFor(null); setClipboard(d.artefactId) }}>{t('cut_artefact')}</button>
                  {canPublishTemplate && (
                    <button role="menuitem" onClick={() => { setMenuFor(null); setModal({ kind: 'publish-template', artefactId: d.artefactId, title: d.title, artefactType: artefactTypeOf(d.type) }) }}>{t('publish_template')}</button>
                  )}
                  {/* In the template library the artefact IS a template: its author-written
                      description, and its version — read-only, the server manages it. */}
                  {isLibrary && (
                    <button role="menuitem" onClick={() => { setMenuFor(null); setModal({ kind: 'template-description', templateId: d.artefactId, current: templateMeta.get(d.artefactId)?.description ?? '' }) }}>{t('template_edit_description')}</button>
                  )}
                  {isLibrary && templateMeta.has(d.artefactId) && (
                    <button role="menuitem" aria-disabled="true" className="rowmenu-info" onClick={(e) => e.preventDefault()}>
                      {t('template_version').replace('{n}', () => String(templateMeta.get(d.artefactId)!.version))}
                    </button>
                  )}
                  {/* "Edit with your own AI…" is hidden — see the `external` Modal kind + ExternalAgentModal
                      below, kept but unreachable, in case this entry point returns. */}
                  <button role="menuitem" className="danger" onClick={() => { setMenuFor(null); setModal({ kind: 'delete-artefact', artefactId: d.artefactId, rooms: d.parts.map((p) => p.name) }) }}>{t('delete')}</button>
                </div>
              )}
            </span>
          )}
        </div>
        {expanded && (
          <div className="parts">
            {/* An artefact can legitimately hold no part (created without a default one, or its
                last part deleted). Expanding onto nothing at all reads as a broken row — say it,
                the way an empty folder does. */}
            {d.parts.length === 0 && (
              <div className="folder-empty" aria-disabled="true">{t('artefact_empty')}</div>
            )}
            {d.parts.map((p, i) => {
              const users = partUsers(p.name)
              const itemMenuId = `part:${p.name}`
              const isDragOver = dropKey === p.name
              return (
                <div
                  key={p.name}
                  className={'part' + (openRoom === p.name ? ' cur' : '') + (isDragOver ? ' drag-over' : '')}
                  draggable={!isSynthetic && !filtering}
                  onDragStart={isSynthetic ? undefined : () => { drag.current = { kind: 'part', artefactId: d.artefactId, index: i } }}
                  onDragOver={isSynthetic ? undefined : (e) => handlePartDragOver(e, d.artefactId, p)}
                  onDragLeave={isSynthetic ? undefined : clearDrop}
                  onDrop={isSynthetic ? undefined : (e) => handlePartDrop(e, d.artefactId, d.parts, i)}
                  onDragEnd={isSynthetic ? undefined : endDrag}
                  onClick={() => { setRootPicked(false); onOpenPart(p.name) }}
                  onContextMenu={isSynthetic || isViewer ? undefined : (e) => openMenuOnContext(itemMenuId, e)}
                >
                  {/* The § range the part spans ("3", or "3-5" when it holds several) — NOT its
                      position in the list. The two used to sit side by side and say the same
                      thing; this one is the address members quote, and the server re-derives it,
                      so a move renumbers the panel too. Empty (but present) for a part with no
                      heading, to keep the titles aligned — which is also every GRID part, since a
                      grid opens on no §. The glyph is what distinguishes one, and it is
                      decorative: the accessible name carries the word. */}
                  <span className="num">{p.section ?? ''}</span>{' '}
                  {p.kind === 'grid' && (
                    <span className="pkind" title={t('grid_label')}>
                      <span aria-hidden="true">▦</span>
                      <span className="visually-hidden">{t('grid_label')} — </span>
                    </span>
                  )}
                  {p.kind === 'slides' && (
                    <span className="pkind" title={t('slides_label')}>
                      <span aria-hidden="true">▭</span>
                      <span className="visually-hidden">{t('slides_label')} — </span>
                    </span>
                  )}
                  <span className="lbl">{partLabel(p)}</span>
                  {/* A read-only part (AB#209): a lock glyph, decorative, with the word in the
                      accessible name — never colour alone. */}
                  {p.readOnly && (
                    <span className="plock" title={t('part_readonly_label')}>
                      <span aria-hidden="true">🔒</span>
                      <span className="visually-hidden"> — {t('part_readonly_label')}</span>
                    </span>
                  )}
                  {users.length > 0 && (
                    <span className="pcount" title={users.join(', ')} aria-label={`${users.length} editing`}>
                      👤 {users.length}
                    </span>
                  )}
                  {!isSynthetic && (
                    <span className="rowmenu-wrap">
                      {!isViewer && (
                      <button
                        className="row-kebab"
                        aria-label={t('item_actions')}
                        aria-haspopup="menu"
                        aria-expanded={menuFor === itemMenuId}
                        onClick={(e) => toggleMenu(itemMenuId, e)}
                      >
                        ⋯
                      </button>
                      )}
                      {menuFor === itemMenuId && (
                        <div className="rowmenu" role="menu" onClick={(e) => e.stopPropagation()}>
                          <button role="menuitem" onClick={() => { setMenuFor(null); setModal({ kind: 'rename-item', room: p.name, current: p.title }) }}>{t('rename')}</button>
                          <button role="menuitem" className="danger" onClick={() => { setMenuFor(null); setModal({ kind: 'delete-item', room: p.name }) }}>{t('delete')}</button>
                        </div>
                      )}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  /** One folder row plus (when expanded) its subfolders and artefacts. Recursive, 5 levels max. */
  function renderFolder(node: FolderNode) {
    const { folder } = node
    // A search shows its matches expanded — collapsed hits would be a list of empty folders.
    const stored = folder.folderId in folderOpen ? folderOpen[folder.folderId]! : true
    const expanded = filtering || stored
    const selected = selectedFolder === folder.folderId
    const menuId = `folder:${folder.folderId}`
    const count = node.children.length + node.artefacts.length
    /**
     * First click selects (this is now where new folders/artefacts go), second expands or
     * collapses. Selecting a folder while looking for a destination should not also have to
     * rearrange the tree.
     */
    const activate = (): void => {
      if (!selected) setSelectedFolder(folder.folderId)
      else setFolderOpen((prev) => ({ ...prev, [folder.folderId]: !expanded }))
    }
    return (
      <div key={folder.folderId} className="folder">
        <div
          className={
            'item folder-row' +
            (selected ? ' selected' : '') +
            (dropKey === menuId ? (folderZone === 'before' ? ' drop-before' : folderZone === 'after' ? ' drop-after' : ' drop-into') : '')
          }
          draggable={!filtering}
          onDragStart={() => { drag.current = { kind: 'folder', folderId: folder.folderId } }}
          onDragEnd={endDrag}
          onDragOver={(e) => handleFolderDragOver(e, node)}
          onDragLeave={clearDrop}
          onDrop={(e) => handleFolderDrop(e, node)}
          onContextMenu={isViewer ? undefined : (e) => openMenuOnContext(menuId, e)}
        >
          {/* The select / expand affordance is a REAL button spanning the row, and the ⋯ menu is
              its sibling: a control nested inside another control is `nested-interactive`, and the
              inner one stops being reliably reachable. Native button = keyboard + AT for free. */}
          <button
            type="button"
            className="folder-toggle"
            aria-expanded={expanded}
            aria-current={selected ? 'true' : undefined}
            onClick={activate}
          >
            <span className={'twisty' + (expanded ? ' open' : '')} aria-hidden="true">▸</span>
            <span className="gi">{expanded ? '📂' : '📁'}</span>
            <span className="lbl">{folder.title}</span>
            <span className="ct">{count}</span>
          </button>
          <span className="rowmenu-wrap">
            {!isViewer && (
            <button
              className="row-kebab"
              aria-label={t('folder_actions')}
              aria-haspopup="menu"
              aria-expanded={menuFor === menuId}
              onClick={(e) => toggleMenu(menuId, e)}
            >
              ⋯
            </button>
            )}
            {/* No "new subfolder" here: selecting the folder and pressing the header's folder
                button is the one way to create, wherever in the tree you are. */}
            {menuFor === menuId && (
              <div className="rowmenu" role="menu" onClick={(e) => e.stopPropagation()}>
                <button role="menuitem" onClick={() => { setMenuFor(null); setModal({ kind: 'rename-folder', folderId: folder.folderId, current: folder.title }) }}>{t('rename')}</button>
                {clipboard && (
                  <button role="menuitem" onClick={() => { setMenuFor(null); void placeArtefact(clipboard, folder.folderId) }}>{t('paste_here')}</button>
                )}
                <button role="menuitem" className="danger" onClick={() => { setMenuFor(null); setModal({ kind: 'delete-folder', folderId: folder.folderId }) }}>{t('delete')}</button>
              </div>
            )}
          </span>
        </div>
        {expanded && (
          <div className="folder-children">
            {node.children.map(renderFolder)}
            {node.artefacts.map((a, i) => renderArtefact(a, i, node.artefacts))}
            {count === 0 && (
              <div className="folder-empty" aria-disabled="true">{t('folder_empty')}</div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <aside className="nav" aria-label="Bid navigation" data-help-id="nav-tree">
      <div className="bidcard">
        {/* No bid title here — the breadcrumb above already names the active bid; repeating it
            in this card read as the same opportunity named twice.
            The label sits IN the flex-wrap row with the chips (not on its own line above), so the
            first one or two ride along on the same line whenever the card is wide enough — denser
            than a fixed two-line layout, while still wrapping the rest below when it isn't. */}
        <div className="tags">
          <span className="m">{t('recent_bids_title')}</span>
          {/* Other bids to switch to — NOT the active one: it is what the breadcrumb already
              names. Capped to the 3 most recently visited (lib/recent-bids.ts); "more…" opens the
              full list on the home page rather than growing this card unbounded for a member on
              many bids. */}
          {shownBids.map((b) => (
            <span
              key={b.id}
              className="tag"
              role="button"
              tabIndex={0}
              onClick={() => onSelectBid(b.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onSelectBid(b.id)
              }}
            >
              {b.name}
            </span>
          ))}
          {hasMoreBids && (
            <span
              className="tag tag-more"
              role="button"
              tabIndex={0}
              onClick={() => onNavigate('home')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onNavigate('home')
              }}
            >
              {t('bid_switcher_more')}
            </span>
          )}
        </div>
      </div>

      {/* Dashboard entry → bid recap (BidHome). Labelled to match where it actually lands — the
          breadcrumb's "Dashboard" crumb — rather than the AI Workflow view, which is a
          distinct, gated destination reached from the WorkflowCard on that same page. */}
      <div
        className={'item nav-entry' + (dashboardActive ? ' active' : '')}
        onClick={pickDashboard}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') pickDashboard() }}
        aria-current={dashboardActive ? 'page' : undefined}
      >
        <span className="gi">⬡</span>
        <span className="lbl">{t('crumb_overview')}</span>
      </div>

      {/* Artefacts entry → the explorer's ROOT row: clicking the title deselects the folder and
          goes back to creating at the top level. The same row doubles as the root drop zone
          (dropping a folder or an artefact here is how anything comes back out of a folder) and
          carries the explorer's actions, which is why the row itself is not the click target —
          the title is a button of its own, never nested in another one. */}
      <div
        className={
          'item nav-entry nav-root-row' +
          (!searching && rootHighlighted ? ' active' : '') +
          (dropKey === 'root' ? ' drop-into' : '')
        }
        onDragOver={handleRootDragOver}
        onDragLeave={clearDrop}
        onDrop={handleRootDrop}
      >
        {searching ? (
          /* The search button widened into this field; Escape or ✕ folds it back. */
          <div className="nav-search">
            <span className="nav-search-ico" aria-hidden="true"><IconSearch /></span>
            <input
              type="search"
              className="nav-search-input"
              autoFocus
              value={query}
              aria-label={t('search_explorer')}
              placeholder={t('search_explorer')}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') closeSearch() }}
            />
            <button
              className="nav-add"
              aria-label={t('search_close')}
              title={t('search_close')}
              onClick={closeSearch}
            >
              <IconClose />
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className="nav-root"
              aria-current={rootHighlighted ? 'true' : undefined}
              title={t('select_root')}
              onClick={() => { setSelectedFolder(null); setRootPicked(true) }}
            >
              {/* Hidden from the accessible name: the entry is "Artefacts", not "▤ Artefacts". */}
              <span className="gi" aria-hidden="true">▤</span>
              <span className="lbl">{t('artefacts')}</span>
            </button>
            <span className="nav-entry-actions">
              {clipboard && (
                <button
                  className="nav-add"
                  aria-label={t('paste_root')}
                  title={t('paste_root')}
                  onClick={() => void placeArtefact(clipboard, null)}
                >
                  <IconPaste />
                </button>
              )}
              <button
                className="nav-add"
                aria-label={t('search_explorer')}
                title={t('search_explorer')}
                onClick={() => setSearching(true)}
              >
                <IconSearch />
              </button>
              {/* Both "new" buttons create inside the selected folder — the root when none is. */}
              {!isViewer && (
                <>
                  <button
                    className="nav-add"
                    aria-label={t('new_folder')}
                    title={canAddFolder ? (createIn ? t('create_in_folder') : t('new_folder')) : t(isLibrary ? 'category_max_depth' : 'folder_max_depth')}
                    disabled={!activeBid || !canAddFolder}
                    onClick={() => setModal({ kind: 'new-folder', parentFolderId: createIn })}
                  >
                    <IconFolder />
                  </button>
                  <button
                    className="nav-add"
                    aria-label={t('new_artefact')}
                    title={createIn ? t('create_in_folder') : t('new_artefact')}
                    disabled={!activeBid}
                    onClick={() => setModal({ kind: 'new-artefact' })}
                  >
                    <IconPlus />
                  </button>
                </>
              )}
            </span>
          </>
        )}
      </div>

      {allArtefacts.length === 0 && folders.length === 0 && (
        <div className="item" aria-disabled="true" style={{ color: 'var(--faint)', cursor: 'default' }}>
          <span className="gi">📄</span> <span className="lbl">{outlineLoading ? '…' : t('no_artefacts')}</span>
        </div>
      )}
      {filtering && shown.roots.length === 0 && shown.artefacts.length === 0 && (
        <div className="item" aria-disabled="true" style={{ color: 'var(--faint)', cursor: 'default' }}>
          <span className="lbl">{t('search_no_match')}</span>
        </div>
      )}
      {shown.roots.map(renderFolder)}
      {shown.artefacts.map((d, i) => renderArtefact(d, i, shown.artefacts))}

      {/* Knowledge — the bid KB indexing surface (upload/scan/search). NOT part of the AI
          surface, so it is visible in MVP 1 (see plans/…: "a visible Knowledge area").
          One row, not a heading plus a row: both said the same word, and only the row did
          anything. */}
      {!isLibrary && (
      <div
        className={'item nav-entry' + (knowledgeActive ? ' active' : '')}
        onClick={pickKnowledge}
        role="button"
        tabIndex={0}
        aria-disabled={!activeBid}
        aria-current={knowledgeActive ? 'page' : undefined}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') pickKnowledge() }}
      >
        <span className="gi">◈</span>
        <span className="lbl">{t('crumb_kb')}</span>
      </div>
      )}

      {modal?.kind === 'new-artefact' && (
        <NewArtefactModal
          onSubmit={(v, type, templateId) => createArtefact(v, type, templateId)}
          onFromPowerPoint={(title) => setModal({ kind: 'new-from-pptx', title, folderId: createIn })}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'new-from-pptx' && activeBid && (
        <NewDeckFromPowerPoint
          bid={activeBid}
          title={modal.title}
          folderId={modal.folderId}
          onCreated={() => {
            if (modal.folderId) setFolderOpen((prev) => ({ ...prev, [modal.folderId as string]: true }))
            void refresh()
          }}
          onJobEnd={() => void refresh()}
          onDone={(room) => {
            setModal(null)
            onOpenPart(room)
          }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'add-item' && (
        <PromptModal
          title={t('add_item_title')}
          label={t('item_name')}
          confirmLabel={t('create')}
          // The kind belongs HERE and only here: a part's room holds prose or a grid, and which one
          // is decided at creation (converting a live room would have to migrate its history —
          // markdown offsets on one side, cell addresses on the other).
          //
          // Only the kinds the ARTEFACT'S TYPE allows (AB#197, lib/artefact-type.ts): text (+ grid)
          // in a prose deliverable, slides (+ grid) in a slides one, grid only in a grid one (AB#263)
          // — the server refuses any other
          // with 422 unsupported_for_kind. GRID is still in beta on top of that: only a tester
          // sees it — the server refuses `kind: 'grid'` from anyone else anyway
          // (Services.createPart → assertGridBeta); this is just not offering it.
          choiceLabel={t('grid_item_kind')}
          choices={addableKinds(modal.artefactType, isBeta).map((k) => ({ value: k, label: t(PART_KIND_LABEL[k]) }))}
          // A refusal is already reported by request() (the global error dialog carries the
          // server's own sentence); catching here only stops a second "Unexpected error" on top.
          // The dialog stays open, so the member can pick again.
          onSubmit={(v, kind) => void addItem(modal.artefactId, v, (kind ?? 'markdown') as PartKind).catch(() => undefined)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'rename-artefact' && (
        <PromptModal title={t('rename_artefact_title')} label={t('artefact_name')} initialValue={modal.current} onSubmit={(v) => void renameArtefact(modal.artefactId, v)} onClose={() => setModal(null)} />
      )}
      {modal?.kind === 'rename-item' && (
        <PromptModal title={t('rename_item_title')} label={t('item_name')} initialValue={modal.current} onSubmit={(v) => void renameItem(modal.room, v)} onClose={() => setModal(null)} />
      )}
      {modal?.kind === 'new-folder' && (
        <PromptModal title={t('new_folder_title')} label={t('folder_name')} confirmLabel={t('create')} onSubmit={(v) => void createFolder(modal.parentFolderId, v)} onClose={() => setModal(null)} />
      )}
      {modal?.kind === 'rename-folder' && (
        <PromptModal title={t('rename_folder_title')} label={t('folder_name')} initialValue={modal.current} onSubmit={(v) => void renameFolder(modal.folderId, v)} onClose={() => setModal(null)} />
      )}
      {modal?.kind === 'delete-folder' && (
        <ConfirmModal title={t('delete_folder_title')} message={t('confirm_delete_folder')} danger onConfirm={() => void deleteFolder(modal.folderId)} onClose={() => setModal(null)} />
      )}
      {modal?.kind === 'split-parts' && (
        <ConfirmModal
          title={t('split_parts_title')}
          message={t('confirm_split_parts')}
          confirmLabel={t('split_parts_run')}
          onConfirm={() => void splitParts(modal.artefactId)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'delete-artefact' && (
        <ConfirmModal
          title={t(isLibrary ? 'template_delete_title' : 'delete_artefact_title')}
          message={t(isLibrary ? 'confirm_delete_template' : 'confirm_delete_artefact')}
          danger
          onConfirm={() => void deleteArtefact(modal.artefactId, modal.rooms)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'template-description' && (
        <PromptModal
          title={t('template_description_title')}
          label={t('template_description_label')}
          initialValue={modal.current}
          multiline
          // Confirm needs a non-empty field, so clearing a description is its own action.
          extraLabel={modal.current ? t('template_description_clear') : undefined}
          onExtra={modal.current ? () => void saveTemplateDescription(modal.templateId, '').catch(() => undefined) : undefined}
          onSubmit={(v) => saveTemplateDescription(modal.templateId, v)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'delete-item' && (
        <ConfirmModal title={t('delete_item_title')} message={t('confirm_delete_item')} danger onConfirm={() => void deleteItem(modal.room)} onClose={() => setModal(null)} />
      )}
      {modal?.kind === 'publish-template' && activeBid && (
        <PublishTemplateDialog
          bidId={activeBid}
          artefactId={modal.artefactId}
          artefactTitle={modal.title}
          artefactType={modal.artefactType}
          onPublished={(version) => {
            setModal(null)
            void qc.invalidateQueries({ queryKey: ['artefact-templates'] })
            setToasts((prev) => [
              ...prev,
              { id: `published-${Date.now()}`, actor: '', kind: 'human', message: t('pt_published').replace('{n}', String(version)) },
            ])
          }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'office-templates' && activeBid && (() => {
        const a = allArtefacts.find((x) => x.artefactId === modal.artefactId)
        const type = artefactTypeOf(a?.type)
        if (!a || type === 'grid') return null
        return (
          <ArtefactTemplateDialog
            bid={activeBid}
            artefactId={a.artefactId}
            title={a.title}
            type={type}
            slidesTemplate={a.slidesTemplate ?? null}
            wordTemplate={a.wordTemplate ?? null}
            canEdit={!isViewer}
            onSaved={() => {
              setModal(null)
              setToasts((prev) => [...prev, { id: `art-tpl-${Date.now()}`, actor: '', kind: 'human', message: t('art_tpl_saved') }])
            }}
            onClose={() => setModal(null)}
          />
        )
      })()}
      {/* Only while one is showing: the editor's own Toasts region is labelled the same, and two
          empty "Activity" landmarks side by side would be one too many. */}
      {toasts.length > 0 && (
        <Toasts toasts={toasts} onDismiss={(id) => setToasts((prev) => prev.filter((x) => x.id !== id))} />
      )}
      {modal?.kind === 'external' && activeBid && (
        <ExternalAgentModal
          bid={activeBid}
          artefactId={modal.artefactId}
          title={modal.title}
          parts={modal.parts}
          onClose={() => setModal(null)}
        />
      )}
    </aside>
  )
}
